<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex shrink-0 items-center justify-between space-x-2">
            <h1 class="text-2xl font-semibold text-gray-700">Transports</h1>
            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search by patient..."
                    class="w-72"
                    icon="i-ph-magnifying-glass-duotone"
                />
                <USelectMenu
                    v-model="filters.status"
                    trailing
                    :class="filters.status ? 'w-48' : 'w-36'"
                    :leading-icon="filters.status ? undefined : 'i-ph-funnel-duotone'"
                    :ui="{
                        icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                    }"
                    :options="statusOptions"
                    value-attribute="value"
                    option-attribute="name"
                >
                    <template #label>
                        <template v-if="filters.status">
                            <span class="text-gray-400">Status:</span>
                            <span class="truncate font-medium">{{ statusOptions.find(o => o.value === filters.status)?.name }}</span>
                        </template>
                        <span v-else class="text-gray-400">Status</span>
                    </template>
                    <template #trailing v-if="filters.status">
                        <button @click.stop="filters.status = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
                <USelectMenu
                    v-model="filters.provider"
                    trailing
                    :class="filters.provider ? 'w-52' : 'w-40'"
                    :leading-icon="filters.provider ? undefined : 'i-ph-ambulance-duotone'"
                    :ui="{
                        icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } }
                    }"
                    :options="providerOptions"
                    value-attribute="value"
                    option-attribute="name"
                >
                    <template #label>
                        <template v-if="filters.provider">
                            <span class="text-gray-400">Provider:</span>
                            <span class="truncate font-medium">{{ providerOptions.find(o => o.value === filters.provider)?.name }}</span>
                        </template>
                        <span v-else class="text-gray-400">Provider</span>
                    </template>
                    <template #trailing v-if="filters.provider">
                        <button @click.stop="filters.provider = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
                <UButton to="/transports/create" color="black" icon="i-ph-plus-bold">
                    New Transport
                </UButton>
            </div>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="transports"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-ambulance-duotone', label: 'No transports.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #number-data="{ row }">
                    <ULink :to="`/transports/${row.id}`" class="font-mono hover:underline">{{ row.number.toString().padStart(6, '0') }}</ULink>
                </template>
                <template #customer-data="{ row }">
                    <div class="leading-5">
                        <p>{{ [row.customer?.first_name, row.customer?.middle_name, row.customer?.last_name].filter(Boolean).join(' ') }}</p>
                        <PhoneLabel v-if="row.customer?.phone" :value="row.customer.phone" class="text-sm" />
                    </div>
                </template>
                <template #provider-data="{ row }">
                    <div v-if="row.provider" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="providers" size="sm" :placeholder="row.provider.name" :path="row.provider.avatar" />
                        <span>{{ row.provider.name }}</span>
                    </div>
                </template>
                <template #route-data="{ row }">
                    <div class="leading-5">
                        <p>{{ row.departure_address?.city || row.departure_address?.formatted?.split(',')[0] || '–' }} → {{ row.arrival_address?.city || row.arrival_address?.formatted?.split(',')[0] || '–' }}</p>
                        <span class="text-sm text-gray-500">{{ row.trip_type === 'round_trip' ? 'Round Trip' : 'One Way' }}</span>
                    </div>
                </template>
                <template #scheduled-data="{ row }">
                    <div class="leading-5">
                        <p>{{ dayjs(row.scheduled_date).format('MMM D, YYYY') }}</p>
                        <span class="text-sm text-gray-500">{{ row.scheduled_time?.slice(0, 5) }}</span>
                    </div>
                </template>
                <template #estimated_fee-data="{ row }">
                    <span v-if="row.estimated_fee != null" class="font-mono">${{ row.estimated_fee }}</span>
                    <span v-else class="text-gray-400">–</span>
                </template>
                <template #price-data="{ row }">
                    <span v-if="row.price !== null && row.price !== undefined" class="font-mono">${{ row.price }}</span>
                    <span v-else class="text-gray-400">–</span>
                </template>
                <template #created_by-data="{ row }">
                    <span v-if="row.creator">{{ row.creator.name }}</span>
                    <span v-else class="text-gray-400">–</span>
                </template>
                <template #status-data="{ row }">
                    <div class="flex items-center gap-2">
                        <BadgeTransportStatus :status="row.status" />
                        <UBadge v-if="row.invoice_id" color="indigo" variant="subtle" label="Invoiced" />
                    </div>
                </template>
                <template #actions-data="{ row }">
                    <UDropdown :items="actions(row)">
                        <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                    </UDropdown>
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />

        <UModal v-model="showShareModal">
            <UCard :ui="{ rounded: 'rounded-lg' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Share Transport</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="showShareModal = false" />
                    </div>
                </template>
                <div v-if="selectedTransport" class="space-y-4">
                    <p class="text-sm text-gray-600">
                        This will send an email to <strong>{{ selectedTransport.provider?.name }}</strong> ({{ selectedTransport.provider?.emails?.join(', ') }}) with the transport details and a link to accept or decline.
                    </p>
                    <UAlert
                        icon="i-ph-info-duotone"
                        color="blue"
                        variant="subtle"
                        title="The following details will be shared:"
                    >
                        <template #description>
                            <ul class="mt-1 text-xs space-y-0.5">
                                <li>Patient: {{ [selectedTransport.customer?.first_name, selectedTransport.customer?.middle_name, selectedTransport.customer?.last_name].filter(Boolean).join(' ') }}</li>
                                <li>Scheduled: {{ dayjs(selectedTransport.scheduled_date).format('MMM D, YYYY') }} at {{ selectedTransport.scheduled_time?.slice(0, 5) }}</li>
                                <li>Trip: {{ selectedTransport.trip_type === 'round_trip' ? 'Round Trip' : 'One Way' }}</li>
                                <li v-if="selectedTransport.estimated_fee">Estimated Fee: ${{ selectedTransport.estimated_fee }}</li>
                            </ul>
                        </template>
                    </UAlert>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="showShareModal = false" size="lg" color="white">Cancel</UButton>
                        <UButton @click="confirmShare" size="lg" color="black" :loading="sharing" icon="i-ph-share-fat-fill">Share</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <UModal v-model="showStatusModal">
            <UCard :ui="{ rounded: 'rounded-lg' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">{{ statusModalTitle }}</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="showStatusModal = false" />
                    </div>
                </template>
                <p class="text-sm text-gray-600">
                    Mark transport <strong>#{{ selectedTransport?.number?.toString().padStart(6, '0') }}</strong> as {{ confirmAction }}?
                </p>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="showStatusModal = false" size="lg" color="white">Cancel</UButton>
                        <UButton @click="confirmStatusChange" size="lg" color="black" :loading="confirming">Confirm</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <UModal v-model="showCompleteModal">
            <UCard :ui="{ rounded: 'rounded-lg' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Complete Transport</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="showCompleteModal = false" />
                    </div>
                </template>
                <div v-if="selectedTransport" class="space-y-4">
                    <p class="text-sm text-gray-600">
                        Complete transport <strong>#{{ selectedTransport.number?.toString().padStart(6, '0') }}</strong> and set the final price.
                    </p>
                    <div v-if="selectedTransport.estimated_fee" class="text-xs text-gray-500">
                        Estimated fee: ${{ selectedTransport.estimated_fee }}
                    </div>
                    <UFormGroup label="Final Price ($)" required>
                        <UInput :model-value="completionPrice ?? undefined" @update:model-value="completionPrice = $event ? Number($event) : null" type="number" size="lg" placeholder="Enter final price..." />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="showCompleteModal = false" size="lg" color="white">Cancel</UButton>
                        <UButton @click="confirmComplete" size="lg" color="black" :loading="confirming" :disabled="!completionPrice" icon="i-ph-check-circle-fill">Complete</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <UModal v-model="showCancelModal">
            <UCard :ui="{ rounded: 'rounded-lg' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">Cancel Transport</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="showCancelModal = false" />
                    </div>
                </template>
                <p class="text-sm text-gray-600">
                    Are you sure you want to cancel transport <strong>#{{ selectedTransport?.number?.toString().padStart(6, '0') }}</strong>? This action cannot be undone.
                </p>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="showCancelModal = false" size="lg" color="white">Back</UButton>
                        <UButton @click="confirmCancel" size="lg" color="red" :loading="confirming" icon="i-ph-x-circle-fill">Cancel Transport</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <TransportResendEmailModal
            v-model="showResendModal"
            :shared-at="selectedTransport?.shared_at"
            :submitting="resending"
            @submit="resendTransportEmail"
        />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { watchDebounced } from '@vueuse/core'
import { UserRole } from '~/types/user'
import { TransportStatus, type Transport } from '~/types/transport'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'Transports' })

const client = useSupabaseClient()
const toast = useToast()
const pagination = usePagination()

const loading = ref(false)
const search = ref('')
const transports = ref<Transport[]>([])
const providerOptions = ref<{ value: string; name: string }[]>([])
const filters = ref<{ status?: string; provider?: string }>({})

const { build: buildTransportActions } = useTransportActions()

const selectedTransport = ref<Transport | null>(null)
const showShareModal = ref(false)
const showStatusModal = ref(false)
const showCompleteModal = ref(false)
const showCancelModal = ref(false)
const showResendModal = ref(false)
const sharing = ref(false)
const confirming = ref(false)
const resending = ref(false)
const copyingLink = ref(false)

const statusOptions = [
    { value: TransportStatus.pending, name: 'Pending' },
    { value: TransportStatus.processing, name: 'Processing' },
    { value: TransportStatus.accepted, name: 'Accepted' },
    { value: TransportStatus.declined, name: 'Declined' },
    { value: TransportStatus.completed, name: 'Completed' },
    { value: TransportStatus.cancelled, name: 'Cancelled' },
]

const columns = [
    { key: 'number', label: '#', class: 'w-px' },
    { key: 'customer', label: 'Customer' },
    { key: 'provider', label: 'Provider' },
    { key: 'route', label: 'Route' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'estimated_fee', label: 'Est. Fee', class: 'w-40' },
    { key: 'price', label: 'Price', class: 'w-40' },
    { key: 'created_by', label: 'Created By' },
    { key: 'status', label: 'Status', class: 'w-px' },
    { key: 'actions', class: 'w-px' },
]



const actions = (transport: Transport) => buildTransportActions(transport, {
    onView: t => navigateTo(`/transports/${t.id}`),
    onEdit: t => navigateTo(`/transports/create?id=${t.id}`),
    onViewInvoice: t => t.invoice_id && navigateTo(`/invoices/providers/${t.invoice_id}`),
    onShare: t => { selectedTransport.value = t; showShareModal.value = true },
    onResendEmail: t => { selectedTransport.value = t; showResendModal.value = true },
    onCopyLink: t => copyShareLink(t),
    onAccept: t => { selectedTransport.value = t; confirmAction.value = 'accepted'; showStatusModal.value = true },
    onDecline: t => { selectedTransport.value = t; confirmAction.value = 'declined'; showStatusModal.value = true },
    onComplete: t => { selectedTransport.value = t; completionPrice.value = t.estimated_fee ?? null; showCompleteModal.value = true },
    onCancel: t => { selectedTransport.value = t; showCancelModal.value = true },
})

async function copyShareLink(transport: Transport) {
    if (copyingLink.value) return
    copyingLink.value = true
    try {
        const { url } = await $fetch<{ url: string; expires_at: string }>(`/api/transports/${transport.id}/share-link`)
        await navigator.clipboard.writeText(url)
        toast.add({ title: 'Link copied', description: 'Valid for 30 days.', color: 'green' })
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Failed to copy link', color: 'red' })
    } finally {
        copyingLink.value = false
    }
}

async function resendTransportEmail() {
    if (!selectedTransport.value || resending.value) return
    resending.value = true
    try {
        await $fetch('/api/transports/share', { method: 'POST', body: { id: selectedTransport.value.id } })
        showResendModal.value = false
        toast.add({ title: 'Email resent', color: 'green' })
    } catch (e: any) {
        toast.add({ title: e?.data?.message || 'Failed to resend email', color: 'red' })
    } finally {
        resending.value = false
    }
}

type ConfirmAction = 'accepted' | 'declined'
const confirmAction = ref<ConfirmAction | null>(null)
const completionPrice = ref<number | null>(null)

onMounted(() => {
    fetchData()
    fetchProviders()
})

watchDebounced(search, () => fetchData(true), { debounce: 600 })
watch(filters, () => fetchData(true), { deep: true })
watch(() => pagination.from, () => fetchData())

async function fetchProviders() {
    const { data } = await client.from('providers')
        .select('id, name')
        .is('deleted_at', null)
        .order('name')

    if (data) {
        providerOptions.value = data.map(p => ({ value: p.id, name: p.name }))
    }
}

async function fetchData(resetPagination?: boolean) {
    loading.value = true
    transports.value = []

    if (resetPagination) {
        pagination.page = 0
    }
    pagination.setTotal(undefined)

    let query = client.from('transports')
        .select(`
            id, number, status, trip_type, departure_address, arrival_address, scheduled_date, scheduled_time, estimated_fee, price, invoice_id, created_at,
            customer:customers (id, first_name, middle_name, last_name, phone),
            provider:providers (id, name, emails, avatar),
            creator:users (id, name)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(pagination.from, pagination.to)

    if (search.value) {
        const { data: customerIds } = await client.from('customers')
            .select('id')
            .or(`first_name.ilike.%${search.value}%,last_name.ilike.%${search.value}%`)

        if (customerIds?.length) {
            query = query.in('customer_id', customerIds.map(c => c.id))
        } else {
            loading.value = false
            return
        }
    }

    if (filters.value.status) {
        query = query.eq('status', filters.value.status)
    }

    if (filters.value.provider) {
        query = query.eq('provider_id', filters.value.provider)
    }

    const { count, data } = await query

    if (data) {
        transports.value = data as any
    }

    if (count !== null) {
        pagination.setTotal(count)
    }

    loading.value = false
}

async function confirmShare() {
    if (!selectedTransport.value) return
    sharing.value = true

    try {
        await $fetch('/api/transports/share', {
            method: 'POST',
            body: { id: selectedTransport.value.id },
        })

        toast.add({
            title: 'Transport shared with provider.',
            color: 'green',
            icon: 'i-ph-check-circle-duotone'
        })

        showShareModal.value = false
        fetchData()
    } catch (e) {
        toast.add({
            title: 'Could not share transport, please try again later.',
            color: 'red',
            icon: 'i-ph-warning-duotone'
        })
    }

    sharing.value = false
}

const statusModalTitle = computed(() => {
    const labels: Record<string, string> = {
        accepted: 'Accept Transport',
        declined: 'Decline Transport',
    }
    return labels[confirmAction.value || ''] || ''
})

async function confirmStatusChange() {
    if (!selectedTransport.value || !confirmAction.value) return
    confirming.value = true

    const statusMap: Record<string, TransportStatus> = {
        accepted: TransportStatus.accepted,
        declined: TransportStatus.declined,
    }

    const newStatus = statusMap[confirmAction.value]
    if (!newStatus) return

    const updates: any = { status: newStatus }
    if (newStatus === TransportStatus.accepted) updates.accepted_at = new Date()
    if (newStatus === TransportStatus.declined) updates.declined_at = new Date()

    const { error } = await client.from('transports')
        .update(updates as never)
        .eq('id', selectedTransport.value.id)

    if (error) {
        toast.add({ title: 'Could not update transport.', color: 'red', icon: 'i-ph-warning-duotone' })
    } else {
        showStatusModal.value = false
        fetchData()
    }

    confirming.value = false
}

async function confirmComplete() {
    if (!selectedTransport.value || !completionPrice.value) return
    confirming.value = true

    const { error } = await client.from('transports')
        .update({
            status: TransportStatus.completed,
            completed_at: new Date(),
            price: completionPrice.value,
        } as never)
        .eq('id', selectedTransport.value.id)

    if (error) {
        toast.add({ title: 'Could not complete transport.', color: 'red', icon: 'i-ph-warning-duotone' })
    } else {
        showCompleteModal.value = false
        fetchData()
    }

    confirming.value = false
}

async function confirmCancel() {
    if (!selectedTransport.value) return
    confirming.value = true

    const { error } = await client.from('transports')
        .update({ status: TransportStatus.cancelled, cancelled_at: new Date() } as never)
        .eq('id', selectedTransport.value.id)

    if (error) {
        toast.add({ title: 'Could not cancel transport.', color: 'red', icon: 'i-ph-warning-duotone' })
    } else {
        showCancelModal.value = false
        fetchData()
    }

    confirming.value = false
}
</script>
